import { CONSIGNMENT_STATUS, ConsignmentStatusCode } from '../config/constants';

/**
 * Business logic for consignment permissions
 * These are pure functions that determine what can be edited based on status and state
 */

/**
 * Permissions for consignment-level operations
 */
export interface ConsignmentPermissions {
  canEdit: boolean;
  canDelete: boolean;
  canChangeStatus: boolean;
  canAddAttachments: boolean;
}

/**
 * Get permissions for consignment operations
 * @param isReadOnly - readonly flag from consignment
 */
export function getConsignmentPermissions(isReadOnly: boolean): ConsignmentPermissions {
  const canEdit = !isReadOnly;

  return {
    canEdit,
    canDelete: canEdit,
    canChangeStatus: canEdit,
    canAddAttachments: canEdit
  };
}

/**
 * Check if a consignment line can be added/edited based on status and first receive
 * This is the base rule without considering user roles
 */
export function isLineEditableByStatus(statusCode: ConsignmentStatusCode, hasFirstReceive: boolean): boolean {
  // NEW or ACCEPTED - always editable
  if (statusCode === CONSIGNMENT_STATUS.NR || statusCode === CONSIGNMENT_STATUS.AC) {
    return true;
  }

  // IN_PROGRESS - editable only if no first receive
  if (statusCode === CONSIGNMENT_STATUS.IP) {
    return !hasFirstReceive;
  }

  // All other statuses - not editable
  return false;
}

/**
 * Permissions matrix for consignment line operations
 */
export interface ConsignmentLinePermissions {
  canAddLine: boolean;
  canEditLine: boolean;
  canDeleteLine: boolean;
  canEditQuantityFields: boolean; // inventory and handling unit quantities
  canEditOtherFields: boolean; // all other fields
}

/**
 * Calculate permissions for consignment line operations
 * @param isReadOnly - readonly flag from consignment
 * @param statusCode - consignment status
 * @param hasFirstReceive - whether first warehouse receive has been done for the line
 * @param userRoles - array of user roles
 */
export function getConsignmentLinePermissions(
  isReadOnly: boolean,
  statusCode: ConsignmentStatusCode,
  hasFirstReceive: boolean,
  userRoles: string[] = []
): ConsignmentLinePermissions {
  // If consignment is readonly, nothing can be edited
  if (isReadOnly) {
    return {
      canAddLine: false,
      canEditLine: false,
      canDeleteLine: false,
      canEditQuantityFields: false,
      canEditOtherFields: false
    };
  }

  // Check if user has special roles (single pass through array)
  const hasSpecialRole = userRoles.some((role) => role === 'PRA' || role === 'ADM');

  // Check base editability by status
  const baseEditable = isLineEditableByStatus(statusCode, hasFirstReceive);

  // PRA/ADM users have special rules
  if (hasSpecialRole) {
    return {
      canAddLine: true, // Can always add new lines
      canEditLine: true, // Can edit at any status
      canDeleteLine: true, // Can delete at any status
      canEditQuantityFields: !hasFirstReceive, // Cannot edit quantities after first receive
      canEditOtherFields: true // Can edit other fields at any status
    };
  }

  // Regular users follow status-based rules
  return {
    canAddLine: baseEditable,
    canEditLine: baseEditable,
    canDeleteLine: baseEditable,
    canEditQuantityFields: baseEditable,
    canEditOtherFields: baseEditable
  };
}
